export enum MortgageTransactionSource {
	LLC = 'LLC',
	CLOSER_SYNC = 'CloserSync',
}

export interface TransactionBase extends TransactionStatusBase {
	overview: BaseTransactionOverview;
	id: string;
	matterId?: string | null;
	matterMortgageId?: string | null;
	matterName?: string | null;
	matterNumber?: string | null;
	assignedLawyer: string | null;
	assignedClerk?: string | null;
	clientMappings?: ClientMappings;
	lender: MortgageInstructionLender | null;
	mortgagee?: MortgageInstructionLender | null;
	lastAccessUserId?: string;
	lastAccessTime?: number;
	ammendmentHistory?: { [id: string]: any };
	valid?: boolean;
	latestVersionId: string;
	currentAssignedVersionId: string | null;
}

export interface TransactionStatusBase {
	lenderChanges?: boolean;
	lenderNotes?: boolean;
	milestones?: LLCMilestone[];
	status: MortgageTransactionStatus | null;
	source: MortgageTransactionSource;
	instructionId: string | null;
}

export enum MortgageTransactionStatus {
	PENDING_ACCEPTANCE = 'PENDING ACCEPTANCE',
	DECLINED = 'DECLINED',
	ACTIVE = 'ACTIVE',
	COMPLETED = 'COMPLETED',
	CANCELLATION_REQUESTED = 'CANCELLATION REQUESTED',
	CANCELLED = 'CANCELLED',
	ACCEPTED = 'ACCEPTED',
	REQUEST_FOR_FUNDS_READY_FOR_REVIEW = 'REQUEST_FOR_FUNDS_READY_FOR_REVIEW',
	GENERATING_REQUEST_FOR_FUNDS = 'GENERATING_REQUEST_FOR_FUNDS',
	REQUEST_FOR_FUNDS_REQUEST_SENT = 'REQUEST_FOR_FUNDS_REQUEST_SENT',
	REQUEST_FOR_FUNDS_SUBMITTED = 'REQUEST_FOR_FUNDS_SUBMITTED',
	REQUEST_FOR_FUNDS_ACCEPTED = 'REQUEST_FOR_FUNDS_ACCEPTED',
	REQUEST_FOR_FUNDS_REJECTED = 'REQUEST_FOR_FUNDS_REJECTED',
	GENERATING_FINAL_REPORT = 'GENERATING_FINAL_REPORT',
	FINAL_REPORT_READY_FOR_REVIEW = 'FINAL_REPORT_READY_FOR_REVIEW',
	FINAL_REPORT_REQUEST_SENT = 'FINAL_REPORT_REQUEST_SENT',
	FINAL_REPORT_SUBMITTED = 'FINAL_REPORT_SUBMITTED',
	FINAL_REPORT_ACCEPTED = 'FINAL_REPORT_ACCEPTED',
	FINAL_REPORT_REJECTED = 'FINAL_REPORT_REJECTED',
}

export interface LLCMilestone {
	id: string;
	name: LLCMilestoneName;
	description: string;
	completedDateTime: string;
	sequence: string;
	enabled: boolean;
}

export enum LLCMilestoneName {
	ACCEPTED = 'ACCEPTED',
	PRELIMINARY = 'PRELIMINARY',
	FINAL = 'FINAL',
	BROKER_SATISFIED = 'BROKER_SATISFIED',
	SOLICITOR_SATISFIED = 'SOLICITOR_SATISFIED',
	LENDER_AUTH_TO_FUND = 'LENDER_AUTH_TO_FUND',
	DEAL_FUNDED = 'DEAL_FUNDED',
}

export interface Lawyer {
	firstName: string;
	middleName?: string;
	lastName: string;
	userName?: string;
}

export interface BaseTransactionOverview {
	transactionId: string;
	lenderCode: string;
	transitNumber: string;
	lenderReferenceNumber: string;
	mortgageAmount: number;
	closingDate: string;
	createdDateTime: string;
	mortgagor?: MortgageInstructionPerson;
	transactionType?: string;
	lawyer: Lawyer;
	vendor?: Vendor;
	workflowModelId?: string;
	sourceFctUrn?: string;
	propertyAddress: BaseTransactionAddress;
	mortgagors: MortgageInstructionMortgagor[];
	guarantors: MortgageInstructionGuarantor[];
	lenderName?: string;
	lenderShortName?: string;
	logoUrl?: string;
	uploadedAt?: number;
}

export interface Vendor {
	vendorType: string;
	companyName: string;
	firstName: string;
	middleName: string;
	lastName: string;
}

export interface BaseTransactionAddress {
	careOf?: string;
	city: string;
	country: string;
	postalCode: string;
	province: string;
	streetAddress1: string;
	streetAddress2?: string;
	streetNumber?: string;
	unitNumber?: string;
}

export type ClientMappingType = 'company' | 'person' | 'signingOfficer';
export interface ClientMapping {
	closerClientId: string;
	llcId: string | number;
	type: ClientMappingType;
}
export type ClientMappings = ClientMapping[];

export enum MortgageInstructionLenderType {
	BANK = 'bank',
}

export interface MortgageInstructionLender {
	lenderCode: string;
	shortName: string;
	nameEn: string;
	nameFr?: string;
	logoUrl?: string;
	institutionId: string;
	type: MortgageInstructionLenderType;
	globalReferenceId: string;
}

export type MortgagorType = 'BUSINESS' | 'PERSON';

export interface MortgageInstructionMortgagor
	extends MortgageInstructionPerson {
	mortgagorType: MortgagorType;
	priorityIndicator?: string;
	provinceOfIncorporation?: string;
	spouseFirstName?: string;
	spouseIlaRequired?: boolean;
	spouseLastName?: string;
	spouseMiddleName?: string;
	spouseOccupation?: string;
	unsecuredDebts?: UnsecureDebt[];
}

export type GuarrantorType = 'BUSINESS' | 'PERSON';

export interface MortgageInstructionGuarantor
	extends MortgageInstructionPerson {
	guarantorType: GuarrantorType;
}

export interface UnsecureDebt {
	action: string;
	amount: number;
	creditor: string;
}

export interface MortgageInstructionPerson {
	address: BaseTransactionAddress;
	birthDate?: Date;
	businessPhone?: string;
	companyName?: string;
	firstName?: string;
	id: number;
	identifications?: [];
	ilaRequired?: boolean;
	lastName?: string;
	middleName?: string;
	occupation?: string;
	phone?: string;
	signatories?: string;
}
